import { Component, createRef } from 'react';
import { withTranslation } from 'react-i18next';
import html2canvas from 'html2canvas';
import moment from 'moment';

// The Search Console property list has one implementation in the app, on the tool that introduced
// it; every GSC tool picks a property out of everything Search Console exposes to the user, which
// is a wider list than the domains linked to GDDash.
import { getUserDomains } from '../../DomainPerformance/query';
import ScopeControls from '../GscTools/components/ScopeControls';
import { ELIGIBLE_PERMISSIONS } from '../GscTools/query';
import { downloadCsv } from '../GscTools/csv';
import { metricsFor, reportsPosition } from '../GscTools/metrics';

import ExtraControls from './components/ExtraControls';
import Freshness from './components/Freshness';
import HourEvidence from './components/HourEvidence';
import HourlyMovement from './components/HourlyMovement';
import RefreshExport from './components/RefreshExport';
import {
    HOURLY_RETENTION_DAYS,
    WINDOW_HOURS,
    buildHours,
    formatWindow,
    hoursInWindow,
    readHours,
    resolveWindow,
    shiftWindow,
} from './hours';
import { buildHoursCsv } from './exportRows';
import { fetchHours } from './query';
import { comparableTotals } from './rows';
import {
    URL_KEYS,
    hasReportScope,
    initialDraft,
    reportDraftFromParams,
    reportParamsFromScope,
    resolveReportScope,
} from './scope';
import { readParams, writeParams } from '../GscTools/url';

const DEFAULT_METRIC = 'clicks';

/**
 * Latest 24 Hours — recent Web Search movement, with the delay and the unfinished hours in plain
 * sight.
 *
 * This is not a live view and the page works hard not to be read as one. Search Console's hourly
 * feed runs behind the clock by an amount the browser cannot know, and its most recent hours are
 * still being written when they are first returned — so the window is anchored on the newest hour
 * that actually came back rather than on "now", and the trailing hours are marked, kept out of
 * every compared total, and only drawn when the reader asks for them.
 *
 * It is also not a monitor. There is no polling, no auto-refresh and no alert: one fetch when the
 * scope is applied, and another only when someone presses Refresh. A page that reloaded itself
 * would spend a property's request budget unattended and would invite exactly the real-time
 * reading the data cannot support.
 *
 * The comparison is the same hours one week earlier, matched on the exact instant seven days
 * back. Yesterday would be the wrong baseline — 03:00 and 15:00 are different businesses — and
 * matching on hour-of-day instead of on the instant would collapse the two 01:00s of a
 * daylight-saving fall-back into one.
 *
 * One request per fetch, carrying both windows: the comparison is a single claim, and half of it
 * is a different report rather than a weaker one. An older response can never overwrite a newer
 * one — every fetch carries a generation, and a late arrival whose generation has been superseded
 * is dropped rather than painted over the newer figures.
 */
class LatestHours extends Component {
    constructor(props) {
        super(props);

        this.handleDraftChange = this.handleDraftChange.bind(this);
        this.handleApply = this.handleApply.bind(this);
        this.handleRefresh = this.handleRefresh.bind(this);
        this.handleToggleControls = this.handleToggleControls.bind(this);
        this.handleMetric = this.handleMetric.bind(this);
        this.handleExport = this.handleExport.bind(this);
        this.handlePrint = this.handlePrint.bind(this);
        this.handleDownloadImage = this.handleDownloadImage.bind(this);

        this.credentialsDomainId = props.credentials_domain_id || '';
        this.reportRef = createRef();

        // Bumped by every apply and refresh; a response carrying an older value is stale.
        this.generation = 0;
        this.isMounted_ = false;

        const params = readParams();
        this.urlDraft = reportDraftFromParams(params);
        this.urlHasScope = hasReportScope(params);

        this.state = {
            properties: [],
            loadingProperties: true,
            propertiesError: null,

            draft: { ...initialDraft(), ...this.urlDraft },

            applied: null,
            loading: false,
            // Held apart from the data: a failed refresh reports itself without discarding the
            // last good result, which stays on screen labelled with when it was fetched.
            error: null,
            fetchedAt: null,
            controlsOpen: true,
            capturing: false,

            metric: DEFAULT_METRIC,

            // The settled result of the most recent successful fetch, or null before the first.
            data: null,
        };
    }

    async componentDidMount() {
        this.isMounted_ = true;

        let properties = [];
        let propertiesError = null;

        try {
            const sites = await getUserDomains();
            properties = sites.filter((site) => ELIGIBLE_PERMISSIONS.has(site.permissionLevel));
        } catch (err) {
            console.log(err);
            propertiesError = err?.message ?? this.props.t('global.something_went_wrong');
        }

        if (!this.isMounted_) return;

        // A link naming a property this account cannot read is not quietly answered with another
        // property's figures: the first one is selected and the scope bar is left open for the
        // user to confirm, rather than a report being run under a scope nobody asked for.
        const requested = this.urlDraft.siteUrl
            ? properties.find((item) => item.siteUrl === this.urlDraft.siteUrl)
            : null;
        const property = requested ?? properties[0];
        const autoApply = this.urlHasScope && !!property && (!this.urlDraft.siteUrl || !!requested);

        this.setState(
            (state) => ({
                properties,
                propertiesError,
                loadingProperties: false,
                draft: property
                    ? {
                          ...state.draft,
                          siteUrl: property.siteUrl,
                          permissionLevel: property.permissionLevel,
                      }
                    : state.draft,
            }),
            () => {
                if (autoApply) this.handleApply();
            },
        );
    }

    componentWillUnmount() {
        this.isMounted_ = false;
    }

    scope() {
        return resolveReportScope(this.state.draft, this.props.t);
    }

    /** The metric the chart draws, held to the ones Web Search actually reports. */
    metric() {
        return metricsFor(this.state.applied?.searchType ?? 'web').includes(this.state.metric)
            ? this.state.metric
            : DEFAULT_METRIC;
    }

    handleDraftChange(patch) {
        this.setState(
            (state) => ({ draft: { ...state.draft, ...patch } }),
            () => {
                if (!patch.siteUrl) return;

                const property = this.state.properties.find(
                    (item) => item.siteUrl === patch.siteUrl,
                );
                this.setState((state) => ({
                    draft: { ...state.draft, permissionLevel: property?.permissionLevel ?? '' },
                }));
            },
        );
    }

    handleToggleControls() {
        this.setState((state) => ({ controlsOpen: !state.controlsOpen }));
    }

    handleApply() {
        const scope = this.scope();
        if (scope.errors.length) return;
        this.load(scope, { keepControls: false });
    }

    /**
     * Refresh repeats the applied scope. It never reopens the controls and never clears the
     * figures first: until the new response lands, what is on screen is the last successful
     * fetch, and it is still true.
     */
    handleRefresh() {
        if (this.state.applied) this.load(this.state.applied, { keepControls: true });
    }

    /**
     * Fetches one window, resolves which hours it covers, and settles the result.
     *
     * The window is derived here rather than requested: the response says which hour is the
     * newest Search Console has, and the 24 hours ending there are the report. Hours outside it
     * are discarded — the request had to ask in whole calendar days, which overshoot both ends.
     *
     * Applying a *different* coverage mode re-resolves the same rows without refetching; it only
     * changes which of them are totalled and drawn. Going through this method anyway keeps one
     * rule on the page: what is on screen is the result of the last Apply, never a half-applied
     * draft.
     */
    async load(scope, { keepControls }) {
        this.generation += 1;
        const generation = this.generation;

        // Written here rather than in handleApply so the address also follows a refresh, and so
        // it only ever describes a scope that was actually run.
        writeParams(reportParamsFromScope(scope), URL_KEYS);

        this.setState({
            applied: scope,
            controlsOpen: keepControls ? this.state.controlsOpen : false,
            loading: true,
            error: null,
        });

        try {
            const { current, baseline } = await fetchHours({
                credentialsDomainId: this.credentialsDomainId,
                scope,
            });

            if (!this.isMounted_ || generation !== this.generation) return;

            const window = resolveWindow(readHours(current));
            const baselineWindow = shiftWindow(window);
            const currentHours = hoursInWindow(readHours(current), window);
            const baselineHours = hoursInWindow(readHours(baseline), baselineWindow);

            this.setState({
                loading: false,
                error: null,
                fetchedAt: new Date().toISOString(),
                data: {
                    window,
                    baselineWindow,
                    hours: buildHours({ window, currentHours, baselineHours }),
                    baselineReturned: baselineHours.length,
                },
            });
        } catch (err) {
            console.log('hours', err);
            if (!this.isMounted_ || generation !== this.generation) return;

            // The previous result and its fetched-at time are left alone. Block 04 reports the
            // failure beside them rather than the page pretending the refresh never happened.
            this.setState({
                loading: false,
                error: err?.message ?? this.props.t('global.something_went_wrong'),
            });
        }
    }

    handleMetric(metric) {
        this.setState({ metric });
    }

    exportName() {
        const scope = this.state.applied;
        const end = this.state.data?.window?.end;
        return `latest-hours-${scope?.siteUrl ?? 'property'}-${
            end ? end.format('YYYY-MM-DD-HH') : ''
        }`
            .replace(/[^a-z0-9_.-]+/gi, '-')
            .toLowerCase();
    }

    handleExport() {
        const scope = this.state.applied;
        const data = this.state.data;
        if (!scope || !data?.window) return;

        downloadCsv(
            buildHoursCsv({
                t: this.props.t,
                scope,
                window: data.window,
                baselineWindow: data.baselineWindow,
                hours: data.hours,
                fetchedAt: this.state.fetchedAt
                    ? moment(this.state.fetchedAt).format('YYYY-MM-DD HH:mm')
                    : null,
            }),
            `${this.exportName()}.csv`,
        );
    }

    handlePrint() {
        window.print();
    }

    /**
     * Captures the loaded report as an image, from the DOM already on screen — no further request
     * and no upload. The controls and the export actions are left out of the frame, while the
     * freshness and coverage labels stay in it, so the picture still says which hours it is of and
     * which of them were still filling.
     */
    async handleDownloadImage() {
        const container = this.reportRef.current;
        if (!container) return;

        this.setState({ capturing: true });

        try {
            const canvas = await html2canvas(container, {
                backgroundColor: '#ffffff',
                scale: 2,
                ignoreElements: (element) =>
                    element.classList?.contains('gsc-tool-controls') ||
                    element.classList?.contains('gsc-tool-controls-bottom') ||
                    element.classList?.contains('gsc-tool-criteria-actions') ||
                    element.classList?.contains('gsc-tool-page-actions') ||
                    // The shared chart and table carry their own refresh and download buttons.
                    element.classList?.contains('chart-action-btns') ||
                    element.classList?.contains('dt-actions__bar'),
            });

            const link = document.createElement('a');
            link.href = canvas.toDataURL('image/png');
            link.download = `${this.exportName()}.png`;
            link.click();
        } catch (err) {
            console.log(err);
        } finally {
            if (this.isMounted_) this.setState({ capturing: false });
        }
    }

    /** The window the collapsed scope bar states, since this report has no date field. */
    summary() {
        const { t } = this.props;
        const window = this.state.data?.window;

        return window ? (
            <>
                {formatWindow(window)}
                <small>{t('tools.latest_hours.hours', { hours: WINDOW_HOURS })}</small>
            </>
        ) : (
            <>{t('tools.latest_hours.window_pending', { hours: WINDOW_HOURS })}</>
        );
    }

    renderReport(applied) {
        const { t } = this.props;
        const data = this.state.data;

        // The first load has nothing to show yet; a refresh over an existing result keeps it.
        if (!data) {
            return (
                <div className="card table-main rounded-main overflow-hidden gsc-tool-block">
                    <div className="card-body gsc-tool-load-state">
                        <span className="gsc-tool-load-icon" aria-hidden="true">
                            <i className="icon-history" />
                        </span>
                        <h4>{t('tools.latest_hours.loading_title')}</h4>
                        <p>{t('tools.latest_hours.loading')}</p>
                    </div>
                </div>
            );
        }

        // Search Console returned nothing for either of the two days asked for. That is a delay or
        // an unsupported property, never zero traffic, and the page says so instead of drawing an
        // empty chart that reads as a collapse.
        if (!data.window) {
            return (
                <div className="card table-main rounded-main overflow-hidden gsc-tool-block">
                    <div className="card-body gsc-tool-load-state">
                        <span className="gsc-tool-load-icon" aria-hidden="true">
                            <i className="icon-history" />
                        </span>
                        <h4>{t('tools.latest_hours.unavailable_title')}</h4>
                        <p>{t('tools.latest_hours.unavailable')}</p>
                        <p>{t('tools.latest_hours.retention', { days: HOURLY_RETENTION_DAYS })}</p>
                    </div>
                </div>
            );
        }

        const totals = comparableTotals(data.hours, reportsPosition(applied.searchType));

        return (
            <>
                <Freshness
                    scope={applied}
                    window={data.window}
                    baselineWindow={data.baselineWindow}
                    hours={data.hours}
                    baselineReturned={data.baselineReturned}
                    fetchedAt={this.state.fetchedAt}
                />

                <HourlyMovement
                    scope={applied}
                    hours={data.hours}
                    totals={totals}
                    metric={this.metric()}
                    onMetric={this.handleMetric}
                />

                <HourEvidence scope={applied} hours={data.hours} />

                <RefreshExport
                    scope={applied}
                    // A fetch in flight takes the export out of reach until it settles: a file
                    // downloaded mid-refresh would carry the previous window under the new
                    // window's fetched-at time.
                    exportable={!this.state.loading}
                    loading={this.state.loading}
                    error={this.state.error}
                    fetchedAt={this.state.fetchedAt}
                    onRefresh={this.handleRefresh}
                    onExport={this.handleExport}
                />

                <p className="gsc-tool-table-note">
                    {t('tools.latest_hours.footnote', { window: formatWindow(data.window) })}
                </p>
            </>
        );
    }

    render() {
        const { t } = this.props;
        const scope = this.scope();
        const applied = this.state.applied;
        const ready = !!this.state.data?.window;

        return (
            <div className="gsc-tool" ref={this.reportRef}>
                <div className="gsc-tool-title-row">
                    <div>
                        <h2 className="page-title">{t('tools.latest_hours.header')}</h2>
                        <p className="gsc-tool-sub">{t('tools.latest_hours.subtitle')}</p>
                    </div>

                    <div className="gsc-tool-page-actions">
                        <button
                            type="button"
                            className="button button-outline button-primary btn"
                            onClick={this.handleDownloadImage}
                            disabled={!ready || this.state.capturing}
                        >
                            <i className="icon-fi_download" />
                            {this.state.capturing
                                ? t('tools.gsc.exports.capturing')
                                : t('tools.gsc.exports.image')}
                        </button>
                        <button
                            type="button"
                            className="button button-outline button-primary btn"
                            onClick={this.handlePrint}
                            disabled={!ready}
                        >
                            <i className="icon-fi_download" />
                            {t('tools.gsc.exports.full_page')}
                        </button>
                    </div>
                </div>

                {!this.credentialsDomainId && (
                    <div className="gsc-tool-banner error">
                        {t('tools.gsc.controls.no_credentials')}
                    </div>
                )}

                <ScopeControls
                    draft={this.state.draft}
                    scope={scope}
                    applied={applied}
                    open={this.state.controlsOpen}
                    properties={this.state.properties}
                    loadingProperties={this.state.loadingProperties}
                    propertiesError={this.state.propertiesError}
                    loading={this.state.loading}
                    onChange={this.handleDraftChange}
                    onApply={this.handleApply}
                    onRefresh={this.handleRefresh}
                    onToggle={this.handleToggleControls}
                    // No window to pick, no surface to pick, no daily data state to switch: the
                    // bar offers the property and this report's own three controls, and states
                    // the derived window where the date range would otherwise be.
                    fields={['property']}
                    hidePreliminary
                    summary={this.summary()}
                    extraScopeLabels={[
                        t(`tools.latest_hours.comparisons.${scope.comparison}`),
                        t(`tools.latest_hours.coverage_modes.${scope.coverage}`),
                    ]}
                >
                    <ExtraControls draft={this.state.draft} onChange={this.handleDraftChange} />
                </ScopeControls>

                {!applied ? (
                    <div className="card table-main rounded-main overflow-hidden gsc-tool-block">
                        <div className="card-body gsc-tool-load-state">
                            <span className="gsc-tool-load-icon" aria-hidden="true">
                                <i className="icon-history" />
                            </span>
                            <h4>{t('tools.latest_hours.idle_title')}</h4>
                            <p>{t('tools.latest_hours.idle')}</p>
                        </div>
                    </div>
                ) : (
                    this.renderReport(applied)
                )}
            </div>
        );
    }
}

export default withTranslation()(LatestHours);
